import React, { useState } from 'react';
import { isTreatmentStatusSelected } from 'libs/end-to-end/src/treatment';
import { Treatment } from 'libs/end-to-end/src/treatment';
import { TreatmentStatus } from 'libs/end-to-end/src/treatment';
import { Modal } from 'apps/sigma2-ui/src/components/ui/Modal';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { FormValidationError } from 'apps/sigma2-ui/src/components/ui/FormValidationError';
import { RoutableLink } from 'apps/sigma2-ui/src/components/ui/RoutableLink';
import { UpdateTreatmentOptions } from 'apps/sigma2-ui/src/state/observation';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { TreatmentRow } from 'apps/sigma2-ui/src/components/TreatmentRow';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';

interface TreatmentRow {
  treatments: Treatment[];
  title?: React.ReactNode;
  description?: React.ReactNode;
}

interface TreatmentSelectionModalProps {
  show: boolean;
  topTreatments: TreatmentRow;
  additionalTreatments?: TreatmentRow;
  title?: string | React.ReactNode;
  maxWidthOverride?: string;
  updateTreatments: (
    treatmentUpdates: Treatment[],
    options?: UpdateTreatmentOptions,
  ) => void;
  onClose: () => void;
}

export const TreatmentSelectionModal: React.FC<
  TreatmentSelectionModalProps
> = ({
  show,
  topTreatments,
  additionalTreatments,
  onClose,
  updateTreatments,
  title,
  maxWidthOverride,
}) => {
  const language = useLanguage();
  const [error, setError] = useState<string | null>('');

  const toggleTreatment = useAsyncCallback(
    async (isAlive, treatment: Treatment) => {
      setError(null);

      const newStatus = isTreatmentStatusSelected(treatment.status)
        ? TreatmentStatus.Values.Available
        : TreatmentStatus.Values.Selected;

      const updateTreatmentsPayload: [Treatment] = [
        {
          ...treatment,
          status: newStatus,
        },
      ];

      updateTreatments(updateTreatmentsPayload, {
        onSuccess: async () => {
          if (!isAlive()) {
            return;
          }
        },
        onError: async () => {
          if (!isAlive()) {
            return;
          }

          setError(language.errorGeneric());
        },
      });
    },
    [updateTreatments, language],
  );

  return (
    <Modal
      show={show}
      onClose={onClose}
      title={title ?? language.treatmentSelectionHeading()}
      maxWidthOverride={maxWidthOverride}
    >
      <div className="space-y-10">
        <Spacing
          size="lg"
          direction="vertical"
          className="overflow-y-auto px-5 max-h-[70vh] pb-2"
        >
          <TreatmentRow
            title={topTreatments.title}
            description={topTreatments.description}
            treatments={topTreatments.treatments}
            toggleTreatment={toggleTreatment}
            disabled={false}
          />

          {additionalTreatments && (
            <TreatmentRow
              title={additionalTreatments.title}
              description={additionalTreatments.description}
              treatments={additionalTreatments.treatments}
              toggleTreatment={toggleTreatment}
              disabled={false}
            />
          )}
        </Spacing>

        <div className="flex justify-between w-full">
          <div className="ml-3">
            {error && (
              <FormValidationError>
                <p>{error}</p>
              </FormValidationError>
            )}
          </div>

          <RoutableLink to={'/treatment-center'}>
            <S2Typography
              variant="link"
              translatedContent={language.treatmentCartCheckoutCta()}
            />
          </RoutableLink>
        </div>
      </div>
    </Modal>
  );
};
